import React from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import Shimmer from 'ui-components/Shimmer';

// Placeholder for the Deal Flow list (<AsyncTableListing />).
//
// NOT ui-components/TableListingSkeleton: that one is built for startup-web's
// table (18px 28px cells on a #F4F2FE header) and is byte-identical in both
// apps, so it must not be bent to fit here. investor-web's table geometry comes
// from `.table-container` in index.scss and is different:
//
//   thead th  background #eae8fb, padding 0.75rem 1rem, 1rem/600
//   tbody td  padding 1rem, border-bottom 1px solid #ddd, 14px/20px
//
// Every number below is taken from those rules or from the cell renderers in
// startup_applications/index.js, so the shimmering table is the same height as
// the real one and nothing shifts when the rows land.
const HEADER_BG = '#eae8fb';
const HEADER_PADDING = '0.75rem 1rem';
const BODY_PADDING = '1rem';
const ROW_BORDER = '1px solid #ddd';

// The tallest thing in a body row is the Name cell's ImageAvatar (2.5rem = 40px,
// radius 9 - a rounded square, not a circle). It sets the row height, so every
// other cell is centred against it.
const AVATAR = 40;
const AVATAR_RADIUS = 9;

// td line-height is 20px; a text bar sits inside that box so single-line cells
// occupy exactly one line of the real table.
const LINE_BOX = 20;
const TEXT_BAR = 12;

// What each column's cell actually renders. Anything not listed is plain text.
// Keyed by the accessor from InProgressColumns / RejectedColumns, or by the
// column `id` - AsyncTableListing keys its file-icon cell on id, not accessor.
const KIND_BY_ACCESSOR = {
  checkbox: 'checkbox',
  name: 'identity',
  pitch_reels: 'reel',
  pipeline_status_value: 'status',
  pitch_deck: 'icon',
  links: 'icons',
  actions: 'actions',
  document_name: 'document',
};

// A text bar inside a 20px line box, so it never changes the row height.
const TextCell = ({ width }) => (
  <Box sx={{ height: LINE_BOX, display: 'flex', alignItems: 'center' }}>
    <Shimmer width={width} height={TEXT_BAR} radius={4} />
  </Box>
);

const CellContent = ({ kind, width, hideEmail }) => {
  switch (kind) {
    // MUI Checkbox: an 18px glyph in a 24px control.
    case 'checkbox':
      return <Shimmer width={18} height={18} radius={4} />;

    // ImageAvatar + name (600) over email (0.7rem) - see AsyncTableListing's
    // `name` cell, which renders the avatar itself rather than via a Cell.
    case 'identity':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <Shimmer width={AVATAR} height={AVATAR} radius={AVATAR_RADIUS} />
          <Box sx={{ display: 'grid', gap: '6px', minWidth: 0 }}>
            <Shimmer width={120} height={13} radius={4} />
            {/* Columns flagged hideEmail render the name alone. */}
            {!hideEmail && <Shimmer width={170} height={10} radius={4} />}
          </Box>
        </Box>
      );

    // DocumentCategoryIcon (40px, radius 4) then the document title.
    case 'document':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <Shimmer width={40} height={40} radius={4} />
          <Shimmer width={width} height={13} radius={4} />
        </Box>
      );

    // A readOnly MUI Rating - five 20px stars.
    case 'rating':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          {[0, 1, 2, 3, 4].map((star) => (
            <Shimmer key={star} width={20} height={20} radius={4} />
          ))}
        </Box>
      );

    // A single icon button, where `actions` renders two.
    case 'action':
      return <Shimmer width={24} height={24} radius={6} />;

    // PitchReelButton is a round icon button; cells without a video show "-".
    case 'reel':
      return <Shimmer width={40} height={40} radius={20} />;

    // StartupStatusSelector - a pill-shaped select.
    case 'status':
      return <Shimmer width={110} height={32} radius={16} />;

    // A single MemoizedTooltipIcon (Description / etc.), 24px glyph.
    case 'icon':
      return <Shimmer width={24} height={24} radius={6} />;

    // Website + LinkedIn icons sit side by side.
    case 'icons':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Shimmer width={24} height={24} radius={6} />
          <Shimmer width={24} height={24} radius={6} />
        </Box>
      );

    // RowActions renders two MemoizedTooltipIcons (the AI-shortlist Info glyph
    // and Send Message), right aligned.
    case 'actions':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Shimmer width={24} height={24} radius={6} />
          <Shimmer width={24} height={24} radius={6} />
        </Box>
      );

    default:
      return <TextCell width={width} />;
  }
};

// Header labels are 1rem/600. Approximate the rendered label width from the
// text so the header bars line up with the words they stand in for.
const headerBarWidth = (header) => {
  const label = typeof header === 'string' ? header : '';
  return Math.min(Math.max(label.length * 8, 40), 120);
};

// Body text bars vary a little per column so the table does not read as a grid
// of identical blocks - the real one never does.
const TEXT_WIDTHS = [88, 64, 104, 72, 96, 80];

const justifyFor = (align) => {
  if (align === 'center') return 'center';
  if (align === 'right' || align === 'end') return 'flex-end';
  return 'flex-start';
};

/**
 * @param columns  the SAME array the table gets (InProgressColumns / RejectedColumns),
 *                 so the skeleton follows permission-dependent columns automatically.
 * @param rows     how many placeholder rows to draw (match the page limit).
 * @param actionBarWidth  width of the button AsyncTableListing's `actions` slot
 *                 renders above the table. 0 = no action bar.
 * @param overrides  per-accessor corrections - { kind, hideEmail, textWidth } -
 *                 for tables whose cells differ from the Deal Flow defaults.
 *                 Kept here so callers never have to edit the real columns.
 */
const DealFlowListSkeleton = ({ columns = [], rows = 10, showPagination = true, actionBarWidth = 0, overrides = {}, sx = {} }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const visibleColumns = (columns || []).filter((column) => !column?.hidden);

  const flexFor = (column) => {
    // The checkbox column is a fixed gutter in the real table (Name is offset by
    // 50px when it renders), not a column that shares the leftover width.
    const isCheckbox = column?.accessor === 'checkbox';
    return {
      minWidth: isCheckbox ? 50 : column?.minWidth ?? 100,
      width: isCheckbox ? 50 : undefined,
      flex: isCheckbox ? '0 0 auto' : '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: justifyFor(column?.align),
      minHeight: 0,
    };
  };

  // AsyncTableListing swaps the table for MobileCardList below sm, so the
  // skeleton has to switch at the same breakpoint - a shimmering table on a
  // phone would be the wrong shape and the page would jump on load.
  if (isSmallScreen) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.25, ...sx }}>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              border: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
              borderRadius: '12px',
              backgroundColor: '#fff',
              padding: '0.875rem',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Shimmer width={AVATAR} height={AVATAR} radius={AVATAR_RADIUS} />
              <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: '6px' }}>
                <Shimmer width="60%" height={13} radius={4} sx={{ maxWidth: 170 }} />
                <Shimmer width="80%" height={10} radius={4} sx={{ maxWidth: 200 }} />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '0.625rem 0.75rem',
                borderTop: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
                pt: 1,
              }}
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <Box key={index} sx={{ minWidth: 0 }}>
                  <Shimmer width="55%" height={10} radius={4} sx={{ maxWidth: 70, mb: '4px' }} />
                  <Shimmer width="75%" height={13} radius={4} sx={{ maxWidth: 110 }} />
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 0.5,
                borderTop: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
                pt: 1,
              }}
            >
              <Shimmer width={92} height={34} radius={8} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        bgcolor: 'white',
        // The real table scrolls sideways when its ~13 columns do not fit, so
        // the skeleton has to as well - otherwise it would be the only thing on
        // the page forcing the viewport wider than the screen.
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...sx,
      }}
    >
      {actionBarWidth > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem', position: 'sticky', left: 0 }}>
          <Shimmer width={actionBarWidth} height={44} radius={10} />
        </Box>
      )}

      {/* Sized to its columns, but never narrower than the container. */}
      <Box sx={{ minWidth: '100%', width: 'max-content' }}>
        {/* Header - thead th */}
        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: HEADER_BG, padding: HEADER_PADDING }}>
          {visibleColumns.map((column, index) => (
            <Box key={column?.accessor ?? index} sx={flexFor(column)}>
              {/* 1rem label at the browser's default line-height (~1.2) is a ~20px
                  line box, so the header row lands at 12 + 20 + 12 = 44px like the
                  real thead th. */}
              <Box sx={{ height: 20, display: 'flex', alignItems: 'center' }}>
                <Shimmer
                  width={column?.accessor === 'checkbox' ? 18 : headerBarWidth(column?.Header)}
                  height={14}
                  radius={4}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Body - tbody td */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: BODY_PADDING,
              borderBottom: ROW_BORDER,
              // The avatar sets the row height; matching it here keeps every
              // row the same height as the real one even before content loads.
              minHeight: AVATAR,
            }}
          >
            {visibleColumns.map((column, index) => {
              const override = overrides[column?.accessor] || {};
              return (
                <Box key={column?.accessor ?? index} sx={flexFor(column)}>
                  <CellContent
                    kind={override.kind || KIND_BY_ACCESSOR[column?.id] || KIND_BY_ACCESSOR[column?.accessor] || 'text'}
                    hideEmail={override.hideEmail ?? column?.hideEmail}
                    width={override.textWidth ?? TEXT_WIDTHS[(rowIndex + index) % TEXT_WIDTHS.length]}
                  />
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {showPagination && (
        // Mirrors the real footer: Previous + per-page on the left, page numbers
        // centred, Next on the right. Sticks to the left edge so it stays put
        // while the table scrolls sideways.
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
            padding: { xs: '1rem', md: '1rem 1.5rem' },
            position: 'sticky',
            left: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Shimmer width={110} height={44} radius={10} />
            <Shimmer width={136} height={44} radius={10} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {[0, 1, 2].map((page) => (
              <Shimmer key={page} width={28} height={28} radius={6} />
            ))}
          </Box>
          <Shimmer width={96} height={44} radius={10} />
        </Box>
      )}
    </Box>
  );
};

export default DealFlowListSkeleton;
